import React, { useEffect, useRef } from "react";
import $ from "jquery";
import "datatables.net-bs4/js/dataTables.bootstrap4.js";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";
import "datatables.net-responsive-bs4/js/responsive.bootstrap4.js";
import "datatables.net-responsive-bs4/css/responsive.bootstrap4.css";

const EasyGrid = ({
  cols = [],
  dataset = [],
  pageSize,
  defaultExpand = true,
  isLoading = false,
  handleUserClick,
  responsiveMode = "auto", // auto | scroll | hybrid
  rowClassResolver = null,
  ordering = true
}) => {
  const gridTableRef = useRef(null);
  const tbodyRef = useRef(null);
  const handleUserClickRef = useRef(handleUserClick);

  useEffect(() => {
    handleUserClickRef.current = handleUserClick;
  }, [handleUserClick]);

  useEffect(() => {
    const $table = $(gridTableRef.current);
    if (!$table.length) return;

    if ($.fn.dataTable.isDataTable($table)) {
      try {
        $table.DataTable().clear().destroy(false);
      } catch {}
    }

    if (isLoading) return;

    const normalizedCols = (cols || []).map((c) => ({
      ...c,
      title: c.title || c.sTitle || "",
      data: c.data || c.mData || c.name || "",
      orderable: typeof c.orderable === "boolean" ? c.orderable : true
    }));

    const rowMap = new Map();
    const keys = normalizedCols.map((c) => c.data).filter(Boolean);

    const safeData = dataset.map((row, i) => {
      const key =
        row.keyid ?? row._id ?? row.id ?? row.user_id ?? row.sno ?? `row_${i}`;
      const out = { _dt_key: String(key) };
      keys.forEach((k) => {
        if (k in row) out[k] = row[k];
      });
      rowMap.set(String(key), row);
      return out;
    });

    const initialOrder = [];
    normalizedCols.forEach((col, idx) => {
      if (col.order && ["asc", "desc"].includes(col.order.toLowerCase())) {
        initialOrder.push([idx, col.order]);
      }
    });

    // APPLY RESPONSIVE MODE
    // Desktop: no horizontal scroll by default
    // Mobile: enable horizontal scroll when responsiveMode is "scroll" or "hybrid"
    const isMobile = window.matchMedia("(max-width: 640px)").matches;

    let dtResponsive;
    let dtScrollX;

    if (responsiveMode === "auto") {
      dtResponsive = { details: false };
      dtScrollX = false;
    }

    if (responsiveMode === "scroll") {
      dtResponsive = false;
      dtScrollX = true;
    }

    if (responsiveMode === "hybrid") {
      dtResponsive = false;
      dtScrollX = true;
    }

    // ensure scrollX is only active on mobile when intended
    dtScrollX = Boolean(dtScrollX && isMobile);

    let dt;
    try {
      dt = $table.DataTable({
        data: safeData,
        columns: normalizedCols,
        responsive: dtResponsive,
        scrollX: dtScrollX,
        paging: false,
        searching: false,
        info: false,
        pageLength: pageSize || 10,
        lengthChange: false,
        autoWidth: false,
        ordering: ordering,
        order: initialOrder,
        language: {
          paginate: { previous: "&laquo;", next: "&raquo;" },
          emptyTable: "No data available in table"
        },
        orderClasses: false,
        drawCallback: function () {
          $(this.api().table().body())
            .find("td.dt-empty")
            .closest("tr")
            .css("background", "#f7f7f7");
          $(this.api().table().body())
            .find("td.dt-empty")
            .css("background", "#f7f7f7");
          var isMobile = window.matchMedia("(max-width:640px)").matches;
          if (!isMobile) {
            $(this.api().table().container())
              .find(".dt-scroll, .dataTables_scroll")
              .css("width", "100%");
          }
          var $scroll = $(this.api().table().container()).find(
            ".dt-scroll, .dataTables_scroll"
          );
          if (
            $scroll.length &&
            !$scroll.parent().hasClass("easygrid-border-wrap")
          ) {
            $scroll.wrap('<div class="easygrid-border-wrap"></div>');
          }
        },
        rowCallback: function (row, data, index) {
          $(row).css({
            background: index % 2 === 0 ? "#ffffff" : "#f2f2f2"
          });

          const originalRow = rowMap.get(String(data._dt_key));
          if (typeof rowClassResolver === "function" && originalRow) {
            const className = rowClassResolver(originalRow, index);
            if (className) {
              $(row).removeClass("row-back row-lay").addClass(className);
              return;
            }
          }
        }
      });
    } catch {
      return;
    }

    $table.on("click", "a.dt-user-link", function (event) {
      event.preventDefault();
      const key = $(this).attr("data-row-key");
      const rowObj = rowMap.get(String(key));
      if (rowObj && typeof handleUserClickRef.current === "function") {
        handleUserClickRef.current(rowObj);
      }
    });

    const safeRecalc = () => {
      try {
        if (dt && dt.responsive) dt.responsive.recalc();
      } catch {}
    };
    dt.on?.("responsive-resize draw", safeRecalc);

    return () => {
      try {
        $table.off("click", "a.dt-user-link");
      } catch {}
      try {
        dt?.off?.("responsive-resize draw", safeRecalc);
      } catch {}
      try {
        if ($.fn.dataTable.isDataTable($table)) {
          $table.DataTable().clear().destroy(false);
        }
      } catch {}
    };
  }, [cols, dataset, pageSize, isLoading, responsiveMode, rowClassResolver]);

  return (
    <div className="easygrid-wrapper" style={{ width: "100%" }}>
      <style>{`
      .easygrid-wrapper table tbody tr.even,
        .easygrid-wrapper table tbody tr.even > td {
          background-color: #ffffff !important;
          background-image: none !important;
        }
        .easygrid-wrapper table tbody tr.odd,
        .easygrid-wrapper table tbody tr.odd > td {
          background-color: #f7f7f7 !important;
          background-image: none !important;
        }

        .easygrid-wrapper table.dataTable tbody td.dt-empty,
        .easygrid-wrapper table.dataTable tbody td.dataTables_empty {
          background: #f7f7f7 !important;
          background-color: #f7f7f7 !important;
          background-image: none !important;
        }
        .easygrid-wrapper table.dataTable tbody tr.odd {
          background: #f7f7f7 !important;
        }
        .row-back td {
          background-color: #cfe8ff !important;
        }

        /* LAY (PINK / RED) */
        .row-lay td {
          background-color: #ffd6d6 !important;

        }
        .easygrid-wrapper {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
          font-weight: 400;
          display: flex;
          justify-content: flex-start;
        }
        .easygrid-wrapper table.dataTable,
        .easygrid-wrapper table.dataTable.mt-2 {
          margin: 0 !important;
          border: 1px solid #dee2e6;
          border-collapse: collapse;
          width: 100%;
        }
        .easygrid-wrapper table thead th,
        .easygrid-wrapper table tbody td {
          border: 2px solid #000000;
        }
        .easygrid-wrapper .dt-scroll-foot,
        .easygrid-wrapper .dataTables_scrollFoot {
          display: none !important;
        }
        .easygrid-wrapper .dt-container > .row:not(.dt-layout-table),
        .easygrid-wrapper .dataTables_wrapper > .row:not(.dt-layout-table),
        .easygrid-wrapper .dt-container > .dt-layout-row:not(.dt-layout-table),
        .easygrid-wrapper .dataTables_wrapper > .dt-layout-row:not(.dt-layout-table) {
          display: none !important;
        }
        .easygrid-wrapper .dataTables_wrapper {
          border: none !important;
          background: none !important;
          width: 100%;
          max-width: 100%;
        }
        .dataTables_wrapper.dt-bootstrap4.no-footer,
        .dt-container.dt-bootstrap4 {
          border: none !important;
          padding: 0 !important;
          width: 100%;
          max-width: 100%;
          margin-right: auto !important;
          margin-left: 0 !important;
        }
        .easygrid-border-wrap {
          display: block;
          width: 100%;
          border: 1px solid #dee2e6;
        }
        .easygrid-wrapper .dt-container .row,
        .easygrid-wrapper .dataTables_wrapper .row {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        .easygrid-wrapper .dt-container .row > [class*="col"],
        .easygrid-wrapper .dataTables_wrapper .row > [class*="col"] {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .easygrid-wrapper div.dt-scroll,
        .easygrid-wrapper div.dataTables_scroll,
        div.dt-scroll,
        div.dataTables_scroll {
          width: 100% !important;
          max-width: 100% !important;
          border: none !important;
        }
        @media (max-width: 640px) {
          .easygrid-wrapper .dt-scroll,
          .easygrid-wrapper .dataTables_scroll {
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            border: none !important;
          }
          .easygrid-wrapper {
            max-width: 100vw;
            overflow-x: auto;
          }
        }
        .dt-scroll-body,
        .dt-scroll-head,
        .dataTables_scrollBody,
        .dataTables_scrollHead {
          border: none !important;
        }
        .easygrid-wrapper table thead th {
          padding: 5px;
          background:#ffffff;
          
        }
        .easygrid-wrapper table tbody td,
        .easygrid-wrapper table thead th {
          text-align: left !important;
          
        }
      
          .easygrid-wrapper table thead th, .easygrid-wrapper table tbody td {
              border: 2px solid #c7cdd4;
              padding:5px;
          }
        .easygrid-wrapper .dt-column-header {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }
        .easygrid-wrapper .dt-column-order {
          display: inline-block;
          order: 2;
        }
        .easygrid-wrapper .dt-column-title {
          order: 1;
        }
        .easygrid-wrapper a {
          text-decoration: none;
        }
        .easygrid-wrapper a:hover {
          text-decoration: underline;
        }
        @media (max-width: 640px) {
          .easygrid-wrapper table.dataTable,
          .easygrid-wrapper table.dataTable.mt-2 {
            width: 100% !important;
            border: 1px solid #dee2e6 !important;
          }
          .easygrid-wrapper table thead th,
          .easygrid-wrapper table tbody td {
            padding: 0px;
            border: 1px solid #dee2e6;
          }
          .easygrid-wrapper .dataTables_scrollBody,
          .easygrid-wrapper .dt-scroll-body {
            overflow-x: auto !important;
          }
          .easygrid-wrapper .dataTables_scrollHead,
          .easygrid-wrapper .dt-scroll-head {
            overflow-x: hidden !important;
          }
        }
        @media (min-width: 641px) {
          .easygrid-wrapper {
            overflow-x: visible !important;
          }
        }

        table.dataTable > tbody > tr > th, table.dataTable > tbody > tr > td {
          padding: 6px 6px;
      }

      table.dataTable > thead > tr > th, table.dataTable > thead > tr > td {
        padding: 4px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.3);
    }

 

 
      
      `}</style>

      <table
        ref={gridTableRef}
        className="table dataTable no-footer dtr-inline display mt-2 site_family"
        style={{ width: "100%" }}
      >
        <thead>
          <tr className="site_family">
            {cols.map((c) => (
              <th key={c.data || c.title} className="site_family">
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={tbodyRef} className="site_family"></tbody>
      </table>
      <style>{`
   table.dataTable.stripe > tbody > tr:nth-child(odd) > *, table.dataTable.display > tbody > tr:nth-child(odd) > * {
    /* box-shadow: inset 0 0 0 9999px rgba(0, 0, 0, 0.023); */
    /* box-shadow: inset 0 0 0 9999px rgba(var(--dt-row-stripe), var(--dt-row-stripe-alpha)); */

    box-shadow: none !important;
}

`}</style>
    </div>
  );
};

export default EasyGrid;
